import { Injectable } from "@angular/core";
import { MonitoringService } from "../monitoring.service";
import { SharedMailboxService } from "../shared-mailbox.service";
import { OutlookService } from "./outlook.service";

export interface SharedContextResult {
  isShared: boolean;
  targetMailbox?: string;         // shared mailbox SMTP address, always present when isShared is true
  delegatePermissions?: number;   // Office.MailboxEnums.DelegatePermissions bitmask, present when isShared is true
  detectionSkipped?: true;        // set when Mailbox 1.8 is unavailable and detection could not run
}

@Injectable({
  providedIn: "root"
})
export class OutlookSharedContextService {
  constructor(
    private readonly outlookService: OutlookService,
    private readonly monitoringService: MonitoringService,
    private readonly sharedMailboxService: SharedMailboxService
  ) {}

  /**
   * Detects the shared mailbox context, updates SharedMailboxService state, and logs telemetry.
   * Returns true when the current mailbox is a shared mailbox.
   */
  async initializeSharedContextAsync(): Promise<boolean> {
    const result = await this.detectSharedContextAsync();

    this.monitoringService.logCustomEvent("SharedContextDetection", {
      isShared: result.isShared,
      detectionSkipped: result.detectionSkipped ?? false,
    });

    if (result.isShared) {
      this.sharedMailboxService.setSharedMailbox(result.targetMailbox!, result.delegatePermissions);
    } else {
      this.sharedMailboxService.clearSharedMailbox();
    }

    return result.isShared;
  }

  async detectSharedContextAsync(): Promise<SharedContextResult> {
    if (!this.outlookService.isRequirementSet18Supported) {
      return { isShared: false, detectionSkipped: true };
    }

    try {
      const result = await new Promise<Office.AsyncResult<Office.SharedProperties>>((resolve) => {
        this.outlookService.mailboxItem.getSharedPropertiesAsync(resolve);
      });

      if (result.value?.owner && result.value?.targetMailbox) {
        return {
          isShared: true,
          targetMailbox: result.value.targetMailbox,
          delegatePermissions: result.value.delegatePermissions,
        };
      }
      return { isShared: false };
    } catch {
      return { isShared: false };
    }
  }
}
